import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Button,
  Form,
  Table,
  Modal,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import numeral from 'numeral';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import dayjs from '../../../lib/day';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import { useUser } from '../../../contexts/UserContext';
import {
  useFetchOnboardingSpecialistMetrics,
  useFetchOnboardingSpecialistMetricClaims,
} from '../../../services/adminMetrics';

const quarterRange = (d) => {
  const month = d.month(); // 0-based
  const qStartMonth = Math.floor(month / 3) * 3;
  const start = d.month(qStartMonth).startOf('month');
  const end = start.add(2, 'month').endOf('month');
  return { start, end };
};

const lastQuarterRange = (d) => {
  const prev = d.subtract(3, 'month');
  return quarterRange(prev);
};

const formatDuration = (seconds) => {
  const s = Number(seconds || 0);
  if (!Number.isFinite(s) || s <= 0) return '—';
  const hours = s / 3600;
  if (hours < 24) return `${numeral(hours).format('0,0.0')} hrs`;
  const days = hours / 24;
  return `${numeral(days).format('0,0.00')} days`;
};

const ColumnHeader = ({ label, tooltip, className = '' }) => (
  <span className={`d-inline-flex align-items-center gap-1 ${className}`}>
    {label}
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`tooltip-${label.replace(/\s+/g, '-').toLowerCase()}`}>{tooltip}</Tooltip>}
    >
      <FontAwesomeIcon
        icon={faInfoCircle}
        className="text-muted"
        style={{ fontSize: '0.75rem', cursor: 'help' }}
      />
    </OverlayTrigger>
  </span>
);

const OnboardingSpecialistMetrics = () => {
  const { user, hasPermission } = useUser();

  const isAdminOverride = hasPermission('reports:onboarding');
  const today = dayjs.utc();

  const [filters, setFilters] = useState({
    preset: 'this_month',
    startDate: today.startOf('month').format('YYYY-MM-DD'),
    endDate: today.endOf('month').format('YYYY-MM-DD'),
    search: '',
  });

  const { isLoading, data } = useFetchOnboardingSpecialistMetrics(
    filters.startDate || undefined,
    filters.endDate || undefined,
    { enabled: isAdminOverride },
  );

  const totals = data?.totals || null;
  const rows = Array.isArray(data?.rows) ? data.rows : [];

  const filteredRows = useMemo(() => {
    const q = String(filters.search || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.name || '').toLowerCase();
      const email = String(r.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [rows, filters.search]);

  const [claimsModal, setClaimsModal] = useState(null); // { userId, name }
  const claimsModalQuery = useFetchOnboardingSpecialistMetricClaims(
    claimsModal?.userId,
    claimsModal?.metric,
    filters.startDate || undefined,
    filters.endDate || undefined,
    { enabled: !!claimsModal?.userId && !!claimsModal?.metric },
  );

  const setPreset = (preset) => {
    const now = dayjs.utc();
    let startDate = '';
    let endDate = '';

    switch (preset) {
      case 'this_month': {
        startDate = now.startOf('month').format('YYYY-MM-DD');
        endDate = now.endOf('month').format('YYYY-MM-DD');
        break;
      }
      case 'last_month': {
        const last = now.subtract(1, 'month');
        startDate = last.startOf('month').format('YYYY-MM-DD');
        endDate = last.endOf('month').format('YYYY-MM-DD');
        break;
      }
      case 'this_quarter': {
        const { start, end } = quarterRange(now);
        startDate = start.format('YYYY-MM-DD');
        endDate = end.format('YYYY-MM-DD');
        break;
      }
      case 'last_quarter': {
        const { start, end } = lastQuarterRange(now);
        startDate = start.format('YYYY-MM-DD');
        endDate = end.format('YYYY-MM-DD');
        break;
      }
      case 'this_year': {
        startDate = now.startOf('year').format('YYYY-MM-DD');
        endDate = now.endOf('year').format('YYYY-MM-DD');
        break;
      }
      case 'all_time':
      default: {
        startDate = '';
        endDate = '';
        break;
      }
    }

    setFilters((prev) => ({
      ...prev,
      preset,
      startDate,
      endDate,
    }));
  };

  const exportCsv = () => {
    const csvConfig = mkConfig({
      filename: `onboarding-specialist-metrics_${filters.startDate || 'all'}_${filters.endDate || 'time'}`,
      useKeysAsHeaders: true,
    });

    const rowsForCsv = filteredRows.map((r) => ({
      Name: r.name || '',
      Email: r.email || '',
      'Assigned Claims': r.assignedClaims || 0,
      'Total Active Assigned Claims': r.activeAssignedClaims || 0,
      'Total Onboarded': r.claimsMovedToInProcess || 0,
      'Avg Processing Time (hours)': Number(r.avgProcessingSeconds || 0) / 3600,
    }));

    const csv = generateCsv(csvConfig)(rowsForCsv);
    download(csvConfig)(csv);
  };

  if (!isAdminOverride) {
    return (
      <PrivateLayout>
        <Panel>
          <h2>Onboarding Specialist Metrics</h2>
          <div>You do not have access to this page.</div>
        </Panel>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <Row className="mb-4">
        <Col>
          <Panel className="pb-3">
            <Row className="align-items-center mb-4">
              <Col>
                <h2 className="mb-1">Onboarding Specialist Metrics</h2>
                <div className="text-muted small">
                  Date range (Onboarding Assigned): {filters.startDate || 'All time'} →{' '}
                  {filters.endDate || 'All time'}
                </div>
              </Col>
              <Col xs={12} md="auto" className="mt-3 mt-md-0">
                <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                  <Form.Control
                    type="search"
                    placeholder="Search by name/email"
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, search: e.target.value }))
                    }
                    style={{ maxWidth: 240 }}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={exportCsv}
                    disabled={!filteredRows.length}
                  >
                    Export CSV
                  </Button>
                </div>
              </Col>
            </Row>

            <div className="mb-4">
              <ButtonGroup>
                {[
                  { key: 'this_month', label: 'This Month' },
                  { key: 'last_month', label: 'Last Month' },
                  { key: 'this_quarter', label: 'This Quarter' },
                  { key: 'last_quarter', label: 'Last Quarter' },
                  { key: 'this_year', label: 'This Year' },
                  { key: 'all_time', label: 'All Time' },
                ].map((p) => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={filters.preset === p.key ? 'primary' : 'outline-secondary'}
                    onClick={() => setPreset(p.key)}
                  >
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>

            <Row className="g-3 align-items-end">
              <Col xs={6} md={3} lg={2}>
                <Form.Label className="small text-muted mb-1">Start</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      preset: 'custom',
                      startDate: e.target.value,
                    }))
                  }
                />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <Form.Label className="small text-muted mb-1">End</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      preset: 'custom',
                      endDate: e.target.value,
                    }))
                  }
                />
              </Col>
              <Col xs={12} md="auto" className="ms-md-auto">
                {/* removed: inclusion badge (no longer accurate with additional metrics) */}
              </Col>
            </Row>
          </Panel>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Panel className="pb-3">
            <h4 className="mb-4">Totals</h4>
            {isLoading ? (
              <div>Loading…</div>
            ) : totals ? (
              <Row className="g-3">
                <Col xs={12} lg={3}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-assigned-claims">
                          Total number of claims assigned to onboarding specialists within the
                          selected date range, regardless of status.
                        </Tooltip>
                      }
                    >
                      <div className="text-muted small fw-bold mb-1 text-uppercase d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                        Assigned Claims
                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.7rem' }} />
                      </div>
                    </OverlayTrigger>
                    <div className="fs-4 fw-bold text-dark">
                      {numeral(totals.assignedClaims || 0).format('0,0')}
                    </div>
                    <div className="text-muted small">all statuses</div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-active-assigned">
                          Claims currently in "Intake" or "Awaiting Medical Records" status. This
                          count is not limited by the date range and shows current active workload.
                        </Tooltip>
                      }
                    >
                      <div className="text-muted small fw-bold mb-1 text-uppercase d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                        Active Assigned Claims
                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.7rem' }} />
                      </div>
                    </OverlayTrigger>
                    <div className="fs-4 fw-bold text-dark">
                      {numeral(totals.activeAssignedClaims || 0).format('0,0')}
                    </div>
                    <div className="text-muted small">intake & awaiting medical records</div>
                  </div>
                </Col>
                <Col xs={12} lg={2}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-total-onboarded">
                          Number of claims that have progressed to "In Process" status or beyond
                          within the selected date range.
                        </Tooltip>
                      }
                    >
                      <div className="text-muted small fw-bold mb-1 text-uppercase d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                        Total Onboarded
                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.7rem' }} />
                      </div>
                    </OverlayTrigger>
                    <div className="fs-4 fw-bold text-dark">
                      {numeral(totals.claimsMovedToInProcess || 0).format('0,0')}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={2}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-avg-processing">
                          Average time from when a claim was assigned for onboarding until it first
                          reached "In Process" status.
                        </Tooltip>
                      }
                    >
                      <div className="text-muted small fw-bold mb-1 text-uppercase d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                        Avg Processing Time
                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.7rem' }} />
                      </div>
                    </OverlayTrigger>
                    <div className="fs-4 fw-bold text-dark">
                      {formatDuration(totals.avgProcessingSeconds || 0)}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={2}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-avg-service-agreement">
                          Average time from when a client registered until they signed their service
                          agreement.
                        </Tooltip>
                      }
                    >
                      <div className="text-muted small fw-bold mb-1 text-uppercase d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                        Avg Time to Sign
                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.7rem' }} />
                      </div>
                    </OverlayTrigger>
                    <div className="fs-4 fw-bold text-dark">
                      {totals.serviceAgreement?.signedCount
                        ? formatDuration(totals.serviceAgreement?.avgSeconds || 0)
                        : '—'}
                    </div>
                    <div className="text-muted small">
                      n={totals.serviceAgreement?.signedCount || 0}
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              <div>No data.</div>
            )}
          </Panel>
        </Col>
      </Row>

      <Row>
        <Col>
          <Panel>
            <h4 className="mb-4">By Onboarding Specialist</h4>
            {isLoading ? (
              <div>Loading…</div>
            ) : (
              <Table hover responsive className="align-middle text-nowrap">
                <thead className="table-light">
                  <tr>
                    <th className="py-3">Name</th>
                    <th className="py-3">Email</th>
                    <th className="py-3 text-center">
                      <ColumnHeader
                        label="Assigned Claims"
                        tooltip="Total claims assigned to this specialist within the selected date range, regardless of status."
                      />
                    </th>
                    <th className="py-3 text-center">
                      <ColumnHeader
                        label="Active Assigned"
                        tooltip="Claims currently in 'Intake' or 'Awaiting Medical Records' status. Not limited by date range."
                      />
                    </th>
                    <th className="py-3 text-center">
                      <ColumnHeader
                        label="Total Onboarded"
                        tooltip="Claims that have progressed to 'In Process' status or beyond within the selected date range."
                      />
                    </th>
                    <th className="py-3">
                      <ColumnHeader
                        label="Avg Processing Time"
                        tooltip="Average time from onboarding assignment to first 'In Process' status."
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No rows.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r) => (
                      <tr key={r.userId}>
                        <td className="py-2 fw-medium">{r.name}</td>
                        <td className="py-2 text-muted">{r.email}</td>
                        <td className="py-2 text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() =>
                              setClaimsModal({ userId: r.userId, name: r.name, metric: 'assigned' })
                            }
                          >
                            {numeral(r.assignedClaims || 0).format('0,0')}
                          </Button>
                        </td>
                        <td className="py-2 text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() =>
                              setClaimsModal({ userId: r.userId, name: r.name, metric: 'active' })
                            }
                          >
                            {numeral(r.activeAssignedClaims || 0).format('0,0')}
                          </Button>
                        </td>
                        <td className="py-2 text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() =>
                              setClaimsModal({ userId: r.userId, name: r.name, metric: 'onboarded' })
                            }
                          >
                            {numeral(r.claimsMovedToInProcess || 0).format('0,0')}
                          </Button>
                        </td>
                        <td className="py-2">{formatDuration(r.avgProcessingSeconds || 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Panel>
        </Col>
      </Row>

      {/* Claims drilldown modal */}
      <Modal show={!!claimsModal} onHide={() => setClaimsModal(null)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>
            {claimsModal?.metric === 'assigned'
              ? 'Assigned Claims'
              : claimsModal?.metric === 'active'
                ? 'Active Assigned Claims'
                : 'Total Onboarded'}{' '}
            — {claimsModal?.name || ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {claimsModalQuery.isLoading ? (
            <div className="text-center py-4">Loading…</div>
          ) : claimsModalQuery.data && Array.isArray(claimsModalQuery.data.claims) ? (
            <Table hover responsive className="align-middle">
              <thead className="table-light">
                {claimsModal?.metric === 'assigned' || claimsModal?.metric === 'active' ? (
                  <tr>
                    <th className="py-2">Claim</th>
                    <th className="py-2">Client</th>
                    <th className="py-2">Email</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Claim Manager</th>
                    <th className="py-2">Onboarding Assigned</th>
                  </tr>
                ) : (
                  <tr>
                    <th className="py-2">Claim</th>
                    <th className="py-2">Client</th>
                    <th className="py-2">Email</th>
                    <th className="py-2">Onboarding Assigned</th>
                    <th className="py-2">In Process At</th>
                    <th className="py-2">Processing Time</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {claimsModalQuery.data.claims.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No claims.
                    </td>
                  </tr>
                ) : (
                  claimsModalQuery.data.claims.map((c) => (
                    <tr key={c.claimId}>
                      {/* Plain text, no link (Nic 2026-10-24): these clients are
                          still in onboarding, so nobody opens their claim page. */}
                      <td className="py-2 text-muted">{c.claimId.slice(0, 8)}…</td>
                      <td className="py-2">
                        {c.userFirstName || ''} {c.userLastName || ''}
                      </td>
                      <td className="py-2 text-muted">{c.userEmail || ''}</td>
                      {claimsModal?.metric === 'assigned' || claimsModal?.metric === 'active' ? (
                        <>
                          <td className="py-2">{c.claimStatus ? String(c.claimStatus) : '—'}</td>
                          <td className="py-2">{c.claimManagerName || '—'}</td>
                          <td className="py-2">
                            {c.onboardingAssignedAt
                              ? dayjs.utc(c.onboardingAssignedAt).format('MM/DD/YYYY HH:mm')
                              : '—'}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-2">
                            {c.onboardingAssignedAt
                              ? dayjs.utc(c.onboardingAssignedAt).format('MM/DD/YYYY HH:mm')
                              : '—'}
                          </td>
                          <td className="py-2">
                            {c.inProcessAt ? dayjs.utc(c.inProcessAt).format('MM/DD/YYYY HH:mm') : '—'}
                          </td>
                          <td className="py-2">{formatDuration(c.processingSeconds || 0)}</td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted text-center py-4">No data.</div>
          )}
        </Modal.Body>
      </Modal>
    </PrivateLayout>
  );
};

export default OnboardingSpecialistMetrics;


